import { PropertyResponseDto } from "@/common/api/datasources/Api";
import { usePropertiesQuery } from "@/common/api/services/properties";
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
    AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useToast } from "@/hooks/use-toast";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Image, MoreHorizontal, Pencil, Trash, UserCog } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

interface PropertyActionsProps {
    property: PropertyResponseDto;
}

export const PropertyActions = ({ property }: PropertyActionsProps) => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { toast } = useToast();
    const queryClient = useQueryClient();
    const propertiesQuery = usePropertiesQuery();
    const [isAssignManagerOpen, setIsAssignManagerOpen] = useState(false);

    const { mutate: deleteProperty, isPending: isDeleting } = useMutation({
        mutationFn: () => propertiesQuery.deletePropertyMutation.mutationFn(property.propertyUUID),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["properties"] });
            toast({
                title: t("properties.success.deleted"),
            });
        },
        onError: () => {
            toast({
                title: t("properties.errors.deleteError"),
                variant: "destructive",
            });
        },
    });

    const handleEdit = () => {
        navigate(`/properties/${property.propertyUUID}/edit`);
    };

    const handleDelete = () => {
        if (!isDeleting) {
            deleteProperty();
        }
    };

    const handleManageImages = () => {
        navigate(`/properties/${property.propertyUUID}/images`);
    };

    return (
        <>
            <AlertDialog>
                <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                        <Button variant="ghost" className="h-8 w-8 p-0">
                            <span className="sr-only">{t("properties.actions.open")}</span>
                            <MoreHorizontal className="h-4 w-4" />
                        </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={handleEdit}>
                            <Pencil className="mr-2 h-4 w-4" />
                            {t("properties.actions.edit")}
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => setIsAssignManagerOpen(true)}>
                            <UserCog className="mr-2 h-4 w-4" />
                            {t("properties.actions.assignManager")}
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={handleManageImages}>
                            <Image className="mr-2 h-4 w-4" />
                            {t("properties.actions.uploadImages")}
                        </DropdownMenuItem>
                        <DropdownMenuSeparator />
                        <AlertDialogTrigger asChild>
                            <DropdownMenuItem className="text-destructive">
                                <Trash className="mr-2 h-4 w-4" />
                                {t("properties.actions.delete")}
                            </DropdownMenuItem>
                        </AlertDialogTrigger>
                    </DropdownMenuContent>
                </DropdownMenu>

                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>
                            {t("properties.confirmations.delete.title")}
                        </AlertDialogTitle>
                        <AlertDialogDescription>
                            {t("properties.confirmations.delete.description")}
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel>
                            {t("common.cancel")}
                        </AlertDialogCancel>
                        <AlertDialogAction
                            onClick={handleDelete}
                            disabled={isDeleting}
                            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                        >
                            {isDeleting ? t("common.deleting") : t("common.delete")}
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </>
    );
}; 